'use client'

import { useRef, useState, type FormEvent } from 'react'
import { BillingAddressElement, CheckoutProvider, ExpressCheckoutElement, PaymentElement, TaxIdElement, useCheckout } from '@stripe/react-stripe-js/checkout'
import type { StripeExpressCheckoutElementConfirmEvent } from '@stripe/stripe-js'
import { useLocale, useTranslations } from 'next-intl'
import { LockKeyhole } from 'lucide-react'
import { getCheckoutStripe } from '@/lib/stripe-client'
import { useStripeAppearance } from '@/hooks/useStripeAppearance'
import { Link } from '@/i18n/routing'
import { useRouter } from 'next/navigation'

function CheckoutContent () {
  const result = useCheckout()
  const t = useTranslations('SubscriptionCheckout')
  if (result.type === 'loading') return <p role='status'>{t('loading')}</p>
  if (result.type === 'error') return <p role='alert'>{t('loadError')} {result.error.message}</p>
  if (result.checkout.status.type === 'expired') return <p role='alert'>{t('expired')}</p>
  return <CheckoutForm key={result.checkout.id} checkout={result.checkout} />
}

type CheckoutValue = Extract<ReturnType<typeof useCheckout>, { type: 'success' }>['checkout']

function CheckoutForm ({ checkout }: { checkout: CheckoutValue }) {
  const t = useTranslations('SubscriptionCheckout')
  const locale = useLocale()
  const router = useRouter()
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [promoError, setPromoError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [promoBusy, setPromoBusy] = useState(false)
  const confirming = useRef(false)
  const updatingPromotion = useRef(false)
  const recurring = checkout.recurring
  const trial = recurring?.trial
  const taxReady = checkout.tax.status === 'ready'

  const confirm = async (expressEvent?: StripeExpressCheckoutElementConfirmEvent) => {
    if (confirming.current || updatingPromotion.current) {
      // A wallet event must be settled even when we cannot start confirmation.
      // Use a ref: React's busy state may not have rendered yet.
      expressEvent?.paymentFailed({ reason: 'fail', message: t('operationPending') })
      return
    }
    confirming.current = true
    setBusy(true)
    setError(null)
    try {
      const returnPath = `/${locale}/dashboard/upgrade/return?session_id=${encodeURIComponent(checkout.id)}`
      const response = await checkout.confirm({
        // return_url is already set server-side; Stripe rejects setting it twice.
        ...(expressEvent ? { expressCheckoutConfirmEvent: expressEvent } : {}),
        redirect: 'if_required'
      })
      if (response.type === 'error') {
        setError(response.error.message)
      } else {
        // The return page verifies the session server-side and waits for webhooks.
        router.push(returnPath)
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : t('paymentError')
      setError(message)
      expressEvent?.paymentFailed({ reason: 'fail', message })
    } finally {
      confirming.current = false
      setBusy(false)
    }
  }

  const changePromotion = async (remove = false) => {
    if (updatingPromotion.current || confirming.current) return
    updatingPromotion.current = true
    setPromoBusy(true)
    setPromoError(null)
    try {
      const response = remove ? await checkout.removePromotionCode() : await checkout.applyPromotionCode(code.trim())
      if (response.type === 'error') setPromoError(response.error.message)
      else setCode('')
    } catch {
      setPromoError(t('promotionError'))
    } finally {
      updatingPromotion.current = false
      setPromoBusy(false)
    }
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    return confirm()
  }

  return (
    <div className='subscription-checkout' aria-busy={busy || promoBusy}>
      <section className='subscription-checkout__summary' aria-label={t('summary')}>
        {!checkout.livemode && <span className='subscription-checkout__test'>{t('testMode')}</span>}
        <h2>{t('summary')}</h2>
        {checkout.lineItems.map(item => (
          <div className='subscription-checkout__product' key={item.id}>
            <h3>{item.name}</h3>
            {item.description && <p>{item.description}</p>}
          </div>
        ))}
        {trial && <p className='subscription-checkout__trial'>{t('trial', { days: trial.trialPeriodDays })}</p>}
        <dl className='subscription-checkout__totals' aria-live='polite'>
          <div><dt>{t('subtotal')}</dt><dd>{checkout.total.subtotal.amount}</dd></div>
          {checkout.total.discount.minorUnitsAmount > 0 && <div><dt>{t('discount')}</dt><dd>−{checkout.total.discount.amount}</dd></div>}
          <div><dt>{t('tax')}</dt><dd>{taxReady ? checkout.total.taxExclusive.amount : t('taxPending')}</dd></div>
          {checkout.total.appliedBalance.minorUnitsAmount !== 0 && <div><dt>{t('credit')}</dt><dd>{checkout.total.appliedBalance.amount}</dd></div>}
          <div className='subscription-checkout__total'><dt>{t('dueToday')}</dt><dd>{checkout.total.total.amount}</dd></div>
        </dl>
        {recurring && (
          <div className='subscription-checkout__renewal' aria-live='polite'>
            <p>{trial
              ? t('afterTrial', { date: new Intl.DateTimeFormat(locale, { dateStyle: 'long' }).format(new Date(trial.trialEnd * 1000)) })
              : t('renewal')}</p>
            <strong>{recurring.dueNext.total.amount} / {t(`interval_${recurring.interval}`, { count: recurring.intervalCount })}</strong>
            {!taxReady && <p>{t('taxNotice')}</p>}
          </div>
        )}
        <form className='subscription-checkout__promotion' onSubmit={event => { event.preventDefault(); return changePromotion() }}>
          <label htmlFor='subscription-promo'>{t('promotion')}</label>
          <div>
            <input id='subscription-promo' value={code} onChange={event => setCode(event.target.value)} disabled={busy || promoBusy} />
            <button type='submit' disabled={!code.trim() || busy || promoBusy}>{t('apply')}</button>
          </div>
          {checkout.discountAmounts?.map((discount, index) => (
            <p key={`${discount.displayName}-${index}`}>
              {discount.displayName}
              {discount.promotionCode && <button type='button' onClick={() => changePromotion(true)} disabled={busy || promoBusy}>{t('remove')}</button>}
            </p>
          ))}
          {promoError && <p role='alert' className='subscription-checkout__error'>{promoError}</p>}
        </form>
        <p className='subscription-checkout__security'><LockKeyhole size={16} />{t('secure')}</p>
      </section>

      <form className='subscription-checkout__payment' onSubmit={submit}>
        <div inert={busy || promoBusy}>
          <ExpressCheckoutElement onConfirm={event => confirm(event)} onLoadError={() => setError(t('loadError'))} />
        </div>
        <label htmlFor='subscription-email'>{t('email')}</label>
        <input id='subscription-email' type='email' autoComplete='email' value={checkout.email ?? ''} readOnly required />
        <p>{t('accountEmail')}</p>
        <h3>{t('billingAddress')}</h3>
        <BillingAddressElement />
        <TaxIdElement options={{}} />
        <h3>{t('paymentDetails')}</h3>
        <PaymentElement options={{ layout: 'accordion' }} onLoadError={() => setError(t('loadError'))} />
        {error && <p role='alert' className='subscription-checkout__error'>{error}</p>}
        <button className='subscription-checkout__submit' type='submit' disabled={busy || promoBusy || !checkout.email || !checkout.canConfirm}>
          {busy ? t('processing') : trial ? t('startTrial') : t('subscribe', { amount: checkout.total.total.amount })}
        </button>
        <p className='subscription-checkout__terms'>
          {t('consent')} <Link href='/terms' target='_blank' rel='noopener noreferrer'>{t('terms')}</Link> · <Link href='/privacy' target='_blank' rel='noopener noreferrer'>{t('privacy')}</Link>
        </p>
      </form>
    </div>
  )
}

export default function SubscriptionCheckout ({ clientSecret }: { clientSecret: string }) {
  const locale = useLocale() === 'es' ? 'es' : 'en'
  const [stripePromise] = useState(() => typeof window !== 'undefined' ? getCheckoutStripe(locale) : null)
  const appearance = useStripeAppearance()
  return (
    <CheckoutProvider stripe={stripePromise} options={{ clientSecret, elementsOptions: { appearance } }}>
      <CheckoutContent />
    </CheckoutProvider>
  )
}
